import React, { useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Avatar,
} from '@mui/material';
import {
  AlertTriangle,
  X,
  ArrowLeftCircle,
  CheckCircle,
  Circle,
  Clock,
  Ban,
  PlayCircle,
  ExternalLink,
  AlertCircle,
  Lock,
} from 'lucide-react';
import dayjs from 'dayjs';

import { Task, TaskStatus } from '@/types';
import PriorityBadge from '@/shared/components/PriorityBadge';

// ============================================================================
// TYPES
// ============================================================================

interface DependencyBlockedWarningModalProps {
  open: boolean;
  onClose: () => void;
  task: Task;
  blockingTasks: Task[];
  onViewTask: (taskId: string) => void;
  onForceComplete?: () => void; // Optional: allow forcing completion with warning
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

const getStatusIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return <CheckCircle size={16} style={{ color: '#16a34a' }} />;
    case TaskStatus.IN_PROGRESS:
      return <PlayCircle size={16} style={{ color: '#4F46E5' }} />;
    case TaskStatus.BLOCKED:
      return <Ban size={16} style={{ color: '#f59e0b' }} />;
    case TaskStatus.CANCELLED:
      return <Ban size={16} style={{ color: '#ef4444' }} />;
    default:
      return <Circle size={16} style={{ color: '#64748b' }} />;
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.NOT_STARTED:
      return 'Not Started';
    case TaskStatus.IN_PROGRESS:
      return 'In Progress';
    case TaskStatus.COMPLETED:
      return 'Completed';
    case TaskStatus.BLOCKED:
      return 'Blocked';
    case TaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return status;
  }
};

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.COMPLETED:
      return '#16a34a';
    case TaskStatus.IN_PROGRESS:
      return '#4F46E5';
    case TaskStatus.BLOCKED:
      return '#f59e0b';
    case TaskStatus.CANCELLED:
      return '#ef4444';
    default:
      return '#64748b';
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * DependencyBlockedWarningModal
 * 
 * Shows a warning when user tries to complete a task that has incomplete
 * blocking dependencies. Lists all blocking tasks and their status.
 */
const DependencyBlockedWarningModal: React.FC<DependencyBlockedWarningModalProps> = ({
  open,
  onClose,
  task,
  blockingTasks,
  onViewTask,
  onForceComplete,
}) => {
  // Separate completed and incomplete blocking tasks
  const { incompleteTasks, completedTasks } = useMemo(() => {
    return blockingTasks.reduce(
      (acc, t) => {
        if (t.status === TaskStatus.COMPLETED || t.status === TaskStatus.CANCELLED) {
          acc.completedTasks.push(t);
        } else {
          acc.incompleteTasks.push(t);
        }
        return acc;
      },
      { incompleteTasks: [] as Task[], completedTasks: [] as Task[] }
    );
  }, [blockingTasks]);

  // Check if any blocking task is overdue
  const hasOverdueTasks = useMemo(() => {
    return incompleteTasks.some(
      (t) => dayjs(t.dueDate).isBefore(dayjs(), 'day')
    );
  }, [incompleteTasks]);

  const handleViewTask = (taskId: string) => {
    onViewTask(taskId);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 },
      }}
    >
      {/* Header */}
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'warning.100',
                color: 'warning.dark',
              }}
            >
              <Lock size={20} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={600}>
                Cannot Complete Task
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This task has incomplete dependencies
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {/* Warning Alert */}
        <Alert
          severity="warning"
          icon={<AlertTriangle size={20} />}
          sx={{ mb: 3 }}
        >
          <Typography variant="body2" fontWeight={500}>
            Task "{task.title}" is blocked by {incompleteTasks.length} incomplete task(s)
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            Complete the blocking tasks first, or remove the dependencies to proceed.
          </Typography>
        </Alert>

        {/* Overdue Warning */}
        {hasOverdueTasks && (
          <Alert
            severity="error"
            icon={<AlertCircle size={20} />}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              Some blocking tasks are overdue and need immediate attention!
            </Typography>
          </Alert>
        )}

        {/* Incomplete Blocking Tasks */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ArrowLeftCircle size={16} style={{ color: '#f59e0b' }} />
            Blocking Tasks ({incompleteTasks.length} incomplete)
          </Typography>
          <List disablePadding>
            {incompleteTasks.map((blockingTask) => {
              const isOverdue = dayjs(blockingTask.dueDate).isBefore(dayjs(), 'day');
              
              return (
                <ListItem
                  key={blockingTask.id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 1.5,
                    mb: 1,
                    bgcolor: isOverdue ? 'error.50' : 'warning.50',
                    border: 1,
                    borderColor: isOverdue ? 'error.200' : 'warning.200',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      bgcolor: isOverdue ? 'error.100' : 'warning.100',
                    },
                  }}
                  onClick={() => handleViewTask(blockingTask.id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {getStatusIcon(blockingTask.status)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: 250,
                          }}
                        >
                          {blockingTask.title}
                        </Typography>
                        <PriorityBadge priority={blockingTask.priority} size="small" />
                        {isOverdue && (
                          <Chip
                            label="Overdue"
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              bgcolor: 'error.main',
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                        <Chip
                          label={getStatusLabel(blockingTask.status)}
                          size="small"
                          sx={{
                            height: 20,
                            fontSize: '0.65rem',
                            bgcolor: `${getStatusColor(blockingTask.status)}20`,
                            color: getStatusColor(blockingTask.status),
                          }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          Due: {dayjs(blockingTask.dueDate).format('MMM D, YYYY')}
                        </Typography>
                      </Box>
                    }
                  />
                  <Tooltip title="View task">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewTask(blockingTask.id);
                      }}
                    >
                      <ExternalLink size={16} />
                    </IconButton>
                  </Tooltip>
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Completed Blocking Tasks (if any) */}
        {completedTasks.length > 0 && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircle size={16} style={{ color: '#16a34a' }} />
              Completed Dependencies ({completedTasks.length})
            </Typography>
            <List disablePadding>
              {completedTasks.map((completedTask) => (
                <ListItem
                  key={completedTask.id}
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    mb: 0.5,
                    bgcolor: 'success.50',
                    border: 1,
                    borderColor: 'success.200',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'success.100',
                    },
                  }}
                  onClick={() => handleViewTask(completedTask.id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircle size={16} style={{ color: '#16a34a' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body2"
                        sx={{
                          textDecoration: 'line-through',
                          color: 'text.secondary',
                        }}
                      >
                        {completedTask.title}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {/* Info Box */}
        <Box
          sx={{
            mt: 3,
            p: 2,
            bgcolor: 'grey.50',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'grey.200',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            <strong>What you can do:</strong>
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, mt: 0.5 }}>
            <Typography component="li" variant="caption" color="text.secondary">
              Complete the blocking tasks first
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              Remove the dependency relationship if it's no longer relevant
            </Typography>
            <Typography component="li" variant="caption" color="text.secondary">
              Reassign or escalate the blocking tasks if needed
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <Divider />

      {/* Actions */}
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Close</Button>
        {onForceComplete && (
          <Tooltip title="Not recommended - this will ignore dependency rules">
            <Button
              variant="outlined"
              color="error"
              onClick={onForceComplete}
              startIcon={<AlertTriangle size={16} />}
            >
              Force Complete Anyway
            </Button>
          </Tooltip>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default DependencyBlockedWarningModal;

// ============================================================================
// UTILITY HOOK FOR DEPENDENCY VALIDATION
// ============================================================================

export interface DependencyValidationResult {
  canComplete: boolean;
  blockingTasks: Task[];
  incompleteBlockingCount: number;
}

/**
 * Validates if a task can be completed based on its blocking dependencies
 * @param task The task to validate
 * @param allTasks All available tasks for lookup
 * @returns Validation result with blocking task info
 */
export const validateTaskCompletion = (
  task: Task,
  allTasks: Task[]
): DependencyValidationResult => {
  // Get all tasks that block this task
  const blockedByIds = task.blockedByTaskIds || [];
  const blockingTasks = blockedByIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter((t): t is Task => t !== undefined);

  // Check if any blocking task is incomplete
  const incompleteBlockingTasks = blockingTasks.filter(
    (t) => t.status !== TaskStatus.COMPLETED && t.status !== TaskStatus.CANCELLED
  );

  return {
    canComplete: incompleteBlockingTasks.length === 0,
    blockingTasks,
    incompleteBlockingCount: incompleteBlockingTasks.length,
  };
};

/**
 * Validates if all subtasks are complete before completing parent
 * @param task The parent task
 * @param allTasks All available tasks
 * @returns List of incomplete subtasks
 */
export const validateSubtasksCompletion = (
  task: Task,
  allTasks: Task[]
): Task[] => {
  const subtaskIds = task.subtaskIds || [];
  const subtasks = subtaskIds
    .map((id) => allTasks.find((t) => t.id === id))
    .filter((t): t is Task => t !== undefined);

  return subtasks.filter(
    (t) => t.status !== TaskStatus.COMPLETED && t.status !== TaskStatus.CANCELLED
  );
};
